import React, { useMemo, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  ThemeProvider,
  Chip,
  Tooltip,
  Switch,
  Select,
  MenuItem,
  FormControl,
  OutlinedInput,
  IconButton,
  CircularProgress,
  Typography,
  Alert,
  Paper,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { MaterialReactTable } from 'material-react-table';

import { useAPI } from '../Hooks/useAPIs';
import { useNotification } from '../common/NotificationContext';
import { useReusableTable } from '../common/TablePanel/tablePanel';
import { sortingToOrdering } from '../common/TablePanel/tableUtils';
import { useUrlSorting } from '../Hooks/useUrlSorting';
import './UserList.css';

const arraysEqual = (a, b) => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
};

const AllowedRegionsEdit = ({ row, onChange, isLoading, regions }) => {
  const current = row.original.allowed_regions?.map(r => r.code) || [];
  const [selected, setSelected] = useState(current);

  const handleChange = (event) => {
    let value = event.target.value;
    if (typeof value === 'string') value = value.split(',');

    if (value.includes('ALL')) {
      value = ['ALL'];
    } else if (selected.includes('ALL') && value.length > 1) {
      value = value.filter(v => v !== 'ALL');
    }

    setSelected(value);
    onChange(value);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2">Loading regions...</Typography>
      </Box>
    );
  }

  return (
    <FormControl fullWidth size="small">
      <Select
        multiple
        value={selected}
        onChange={handleChange}
        input={<OutlinedInput />}
        renderValue={(selected) => {
          if (selected.includes('ALL')) {
            return <Chip label="ALL" size="small" color="warning" />;
          }
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((code) => {
                const region = regions.find(r => r.code === code);
                return (
                  <Chip
                    key={code}
                    label={code}
                    size="small"
                    title={region?.name || code}
                  />
                );
              })}
            </Box>
          );
        }}
      >
        {regions.map((region) => (
          <MenuItem
            key={region.code}
            value={region.code}
            disabled={region.code !== 'ALL' && selected.includes('ALL')}
          >
            {region.code} - {region.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const ActiveStatusEdit = ({ row, onChange }) => {
  const [isActive, setIsActive] = useState(row.original.is_active);

  const handleToggle = (e) => {
    const checked = e.target.checked;
    setIsActive(checked);
    onChange(checked);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Switch checked={isActive} onChange={handleToggle} color="success" />
      <span>{isActive ? 'Active' : 'Inactive'}</span>
    </Box>
  );
};


const UserList = ({ activeTheme }) => {
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 500 });
  const [sorting, setSorting] = useUrlSorting([{ id: 'display_name', desc: false }]);
  const { apiCall } = useAPI();
  const queryClient = useQueryClient();
  const notify = useNotification();

  const updateUserMutation = useMutation({
    mutationFn: async ({ email, updates }) => {
      const response = await apiCall('put', `/api/users/${email}/`, updates);
      return response.data;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['user-data'] });
      table.setEditingRow(null);
    },
    onError: (error) => {
      notify({ message: `Error updating user: ${error.message || 'Unknown error'}`, severity: 'error' });
    },
  });

  const regionsQuery = useQuery({
    queryKey: ['regions'],
    queryFn: async () => {
      const resp = await apiCall('GET', '/api/regions');
      return resp.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const userQuery = useQuery({
    queryKey: ['user-data', pagination.pageIndex, pagination.pageSize, sorting],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: (pagination.pageIndex + 1).toString(),
        page_size: pagination.pageSize.toString(),
      });
      const ordering = sortingToOrdering(sorting);
      if (ordering) params.append('ordering', ordering);
      const response = await apiCall('GET', `/api/users?${params}`);
      return response.data;
    },
    keepPreviousData: true,
  });

  const data = useMemo(() => userQuery.data?.results || [], [userQuery.data]);
  const rowCount = userQuery.data?.count || 0;
  const isLoading = userQuery.isFetching;
  const isRegionsLoading = regionsQuery.isLoading;
  const REGIONS = regionsQuery.data || [];

  const handleSaveUser = useCallback(
    ({ values, row, table }) => {
      const original = row.original;
      const updates = {};

      if (values.is_active !== original.is_active) {
        updates.is_active = values.is_active;
      }

      const newRegions = Array.isArray(values.allowed_regions)
        ? values.allowed_regions
        : [];
      const oldRegions = original.allowed_regions?.map(r => r.code) || [];

      const finalNew = newRegions.includes('ALL') ? ['ALL'] : newRegions;
      const finalOld = oldRegions.includes('ALL') ? ['ALL'] : oldRegions;

      if (!arraysEqual(finalNew, finalOld)) {
        updates.allowed_region_codes = finalNew;
      }

      if (Object.keys(updates).length === 0) {
        return;
      }

      updateUserMutation.mutate(
        { email: original.email, updates },
        { context: { table } }
      );
    },
    [updateUserMutation]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: 'email',
        header: 'Email',
        enableEditing: false,
        filterFn: 'includesString',
        enableGrouping: false,
      },
      {
        accessorKey: 'display_name',
        header: 'Name',
        enableEditing: false,
        filterFn: 'includesString',
        enableGrouping: false,
      },
      {
        accessorKey: 'region',
        header: 'Current Region',
        enableEditing: false,
        enableGrouping: false,
        enableGlobalFilter: false,
        filterFn: (row, id, filterValue) => {
          const region = row.getValue(id);
          const code = region?.code?.toLowerCase() || '';
          const name = region?.name?.toLowerCase() || '';
          const fv = filterValue.toLowerCase();
          return code.includes(fv) || name.includes(fv);
        },
        Cell: ({ cell }) => {
          const region = cell.getValue();
          if (!region) return <Typography variant="body2" color="text.secondary">No Region</Typography>;
          return (
            <Tooltip title={region.name}>
              <Chip label={region.code} size="small" color="primary" variant="outlined" />
            </Tooltip>
          );
        },
      },
      {
        accessorKey: 'allowed_regions',
        header: 'Allowed Regions',
        enableEditing: true,
        enableGrouping: false,
        enableGlobalFilter: false,
        muiEditTextFieldProps: () => ({ hidden: true }),
        Cell: ({ cell }) => {
          const regions = cell.getValue() || [];
          if (regions.length === 0) return <Typography variant="body2" color="text.secondary">None</Typography>;
          if (regions.some(r => r.code === 'ALL')) {
            return <Chip label="ALL" color="warning" size="small" />;
          }
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {regions.map((r) => (
                <Chip key={r.code} label={r.code} size="small" title={r.name} />
              ))}
            </Box>
          );
        },
        Edit: ({ row, table }) => {
          const handleChange = (value) => {
            row._valuesCache.allowed_regions = value;
            table.setEditingRow({ ...row, _valuesCache: row._valuesCache });
          };

          return (
            <AllowedRegionsEdit
              row={row}
              onChange={handleChange}
              isLoading={isRegionsLoading}
              regions={REGIONS}
            />
          );
        },
      },
      {
        accessorKey: 'is_active',
        header: 'Active Status',
        enableEditing: true,
        muiEditTextFieldProps: () => ({ hidden: true }),
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue() ? 'Active' : 'Inactive'}
            color={cell.getValue() ? 'success' : 'default'}
            size="small"
          />
        ),
        Edit: ({ row, table }) => {
          const handleChange = (value) => {
            row._valuesCache.is_active = value;
            table.setEditingRow({ ...row, _valuesCache: row._valuesCache });
          };

          return <ActiveStatusEdit row={row} onChange={handleChange} />;
        },
      },
    ],
    [isRegionsLoading, REGIONS]
  );

  const { table } = useReusableTable({
    columns,
    data,
    rowCount,
    isLoading,
    enableRowSelection: false,
    enableRowExpansion: false,
    enableCompareFiles: false,
    enableRowVirtualization: true,
    enableDefaultAddToCart: false,
    enableStickyFooter: true,
    enableBottomToolbar: true,
    enableEditing: true,
    editDisplayMode: 'row',
    onEditingRowSave: handleSaveUser,
    getRowId: (row) => row.email,
    pagination,
    sorting,
    onSortingChange: setSorting,
    manualPagination: true,
    onPaginationChange: setPagination,
    muiPaginationProps: {
      color: 'primary',
      rowsPerPageOptions: [100, 200, 500, 1000],
    },
    muiTableContainerProps: { sx: { flex: 1, maxHeight: '100%' } },
    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <IconButton
          size="small"
          color="primary"
          title="Edit"
          onClick={() => table.setEditingRow(row)}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          color="primary"
          title="View in PeopleFinder"
          onClick={() =>
            window.open(
              `https://peoplefinder.gm.com/Person/Show/${encodeURIComponent(row.original.email.split('@')[0])}/`,
              '_blank',
              'noopener,noreferrer'
            )
          }
        >
          <InfoIcon fontSize="small" />
        </IconButton>
      </Box>
    ),
  });

  const activeCount = useMemo(() => data.filter(u => u.is_active).length, [data]);
  const inactiveCount = useMemo(() => data.filter(u => !u.is_active).length, [data]);

  return (
    <ThemeProvider theme={activeTheme}>
      <Box className="user-page-wrapper">
        {/* Page Header */}
        <Paper className="user-page-header" elevation={0} square>
          <Box className="user-header-title-row">
            <Box className="user-header-title">
              <PeopleIcon className="user-header-icon" />
              <Box>
                <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
                  User Management
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage user access, regions and active status
                </Typography>
              </Box>
            </Box>
            <Box className="user-header-stats">
              <Box className="user-stat-card">
                <PersonIcon className="stat-icon stat-icon--total" />
                <Box>
                  <Typography variant="h6" fontWeight={700} lineHeight={1}>
                    {rowCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Users
                  </Typography>
                </Box>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box className="user-stat-card">
                <VerifiedUserIcon className="stat-icon stat-icon--active" />
                <Box>
                  <Typography variant="h6" fontWeight={700} lineHeight={1} color="success.main">
                    {activeCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Active
                  </Typography>
                </Box>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box className="user-stat-card">
                <VerifiedUserIcon className="stat-icon stat-icon--inactive" />
                <Box>
                  <Typography variant="h6" fontWeight={700} lineHeight={1} color="text.secondary">
                    {inactiveCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Inactive
                  </Typography>
                </Box>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box className="user-stat-card">
                <PublicIcon className="stat-icon stat-icon--region" />
                <Box>
                  <Typography variant="h6" fontWeight={700} lineHeight={1} color="primary.main">
                    {REGIONS.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Regions
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <Alert severity="info" className="user-region-alert" icon={<PublicIcon fontSize="small" />}>
            <strong>Region access rules:&nbsp;</strong>
            USA can access all files &nbsp;·&nbsp; CHN can only access China files &nbsp;·&nbsp; Any other country can access its own country files
          </Alert>
        </Paper>

        {/* Table */}
        <Box className="user-main-container">
          <Box className="user-content">
            <MaterialReactTable table={table} />
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default UserList;